import type { AdminOrganizationDetail } from '../../../../domain/superadmin/types';
import DashboardCard from '../../../dashboard/components/DashboardCard';
import { cn } from '@/lib/utils';
import { SectionEmpty } from './SectionEmpty';

type UsageSectionProps = {
  usage: AdminOrganizationDetail['usage'];
};

function UsageBar({
  label,
  current,
  max,
}: {
  label: string;
  current: number;
  max: number;
}) {
  const unlimited = max === -1;
  const pct = unlimited || max <= 0 ? 0 : Math.min(100, Math.round((current / max) * 100));
  const nearLimit = !unlimited && max > 0 && current / max >= 0.85;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-s2 text-[12px]">
        <span className="font-medium text-secondary">{label}</span>
        <span className="font-mono tabular-nums text-default">
          {current}
          <span className="text-muted"> / {unlimited ? '∞' : max}</span>
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-subtle">
        <div
          className={cn(
            'h-full rounded-full transition-[width]',
            nearLimit ? 'bg-warning' : 'bg-primary',
          )}
          style={{ width: unlimited ? '0%' : `${pct}%` }}
          role="progressbar"
          aria-valuenow={current}
          aria-valuemin={0}
          aria-valuemax={unlimited ? undefined : max}
          aria-label={`${label}: ${current} de ${unlimited ? 'ilimitado' : max}`}
        />
      </div>
    </div>
  );
}

export function UsageSection({ usage }: UsageSectionProps) {
  const rows = [
    { label: 'Contatos', ...usage.contacts },
    { label: 'Funis', ...usage.funnels },
    { label: 'WhatsApp', ...usage.whatsapp },
    { label: 'Usuários', ...usage.users },
  ];

  const allEmpty = rows.every((row) => row.current === 0 && row.max === 0);

  return (
    <DashboardCard title="Uso atual" className="mb-s5">
      {allEmpty ? (
        <SectionEmpty message="Nenhum dado de uso disponível para esta empresa." />
      ) : (
        <ul className="flex flex-col gap-s4">
          {rows.map((row) => (
            <li key={row.label}>
              <UsageBar label={row.label} current={row.current} max={row.max} />
            </li>
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}
